import React, {useEffect, useState} from 'react'
import {Alert, Container, Row, Col, Button, Modal} from 'reactstrap';
import {AvForm, AvField} from 'availity-reactstrap-validation';
import {postDownloadedFile, postMaintenanceSubscription} from '../api';
import {getApiErrorMessage} from '../api/errors';
import CaptchaChallenge, {useCaptcha} from './CaptchaChallenge';

const DownloadModal = ({file, value, close, downloaded, setDownloaded}) => {
  let {title, filename, file_url} = file || {};
  const [error, setError] = useState('');
  const captcha = useCaptcha();

  useEffect(() => {
    if (!value) return;

    const handleEscape = event => {
      if (event.key === 'Escape' || event.keyCode === 27) {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => document.removeEventListener('keydown', handleEscape);
  }, [value, close]);

  const handleValidSubmit = async (_, values) => {
    setError('');

    const recaptchaToken = await captcha.execute('downloaded_file');
    if (!recaptchaToken) return;

    try {
      await postDownloadedFile({
        name: values.name,
        company: values.company,
        email: values.email,
        file: filename,
        file_url: file_url,
        recaptcha_token: recaptchaToken,
        recaptcha_action: 'downloaded_file'
      });
      //downloadFiles(filename, file_url);
      setDownloaded(true);
    } catch (error) {
      setError(getApiErrorMessage(error, 'Unable to request this download. Please try again.'));
    }
  }

  return (
    <div>
      <Modal isOpen={value} toggle={close} keyboard>

        <div className="modal-header">
          <button className="close" type="button" onClick={close}>
            <span>×</span>
          </button>
          <h5 className="modal-title text-center">{title}</h5>
        </div>

        <div className="modal-body mb-1 p-0">
        {!downloaded ? 
          <AvForm onValidSubmit={handleValidSubmit}>
            <div className="px-5 py-3">
              <Row>
                <Col md={6}>
                  <AvField name="name" label="Name" type="text"  placeholder="John Doe" required />
                </Col>
                <Col md={6}>
                  <AvField name="company" label="Company" type="text" placeholder="EDIfly" required />
                </Col>
              </Row>
              <AvField name="email" label="Email Address" type="email" placeholder="john.doe@example.com" required />
              <CaptchaChallenge captcha={captcha} />
            </div>
            <div className="modal-footer p-2">
              <Button color="primary" type="submit">Download</Button>
              <Button color="secondary" onClick={close}>Cancel</Button>
            </div>
            {error && <p className="text-danger text-center mb-3 mt-1">{error}</p>}
          </AvForm>
        : <h4 className="p-3 pt-0">Thank you for downloading this file. A link has been sent to your email address.</h4>}
        </div>
      </Modal>
    </div>
  )
}

const parseMaintenanceDateTime = ({date, time}) => {
  if (!date) return null;

  const dateParts = date.split(/[/-]/).map(value => parseInt(value, 10));
  const [hours = 0, minutes = 0] = (time || '').split(':').map(value => parseInt(value, 10));
  let day;
  let month;
  let year;

  if (dateParts[0] > 31) {
    [year, month, day] = dateParts;
  } else {
    [day, month, year] = dateParts;
  }

  if (!day || !month || !year) return null;

  return new Date(year, month - 1, day, hours || 0, minutes || 0);
};

const getNextMaintenance = scheduledMaintenances => {
  const maintenances = scheduledMaintenances || [];
  const now = new Date();
  const withDateTime = maintenances
    .map(maintenance => ({
      maintenance,
      dateTime: parseMaintenanceDateTime(maintenance)
    }))
    .filter(({dateTime}) => dateTime && !Number.isNaN(dateTime.getTime()));

  const futureMaintenances = withDateTime.filter(({dateTime}) => dateTime >= now);
  const sortedMaintenances = (futureMaintenances.length > 0 ? futureMaintenances : withDateTime)
    .sort((first, second) => first.dateTime - second.dateTime);

  return sortedMaintenances[0] ? sortedMaintenances[0].maintenance : maintenances[0];
};

const formatNextMaintenance = maintenance => {
  if (!maintenance) return 'To be announced';

  const date = maintenance.date || 'Date to be confirmed';
  const time = maintenance.time || 'time to be confirmed';
  const timing = maintenance.length || maintenance.timing || maintenance.duration || 'timing to be confirmed';

  return `${date} at ${time} (${timing})`;
};

const MaintenanceSubscriptionModal = ({value, close}) => {
  const [status, setStatus] = useState();
  const [errors, setErrors] = useState([]);
  const captcha = useCaptcha();

  const handleValidSubmit = async (_, values) => {
    const recaptchaToken = await captcha.execute('maintenance_subscription');
    if (!recaptchaToken) return;

    setStatus('submitting');
    setErrors([]);

    try {
      await postMaintenanceSubscription({
        ...values,
        recaptcha_token: recaptchaToken,
        recaptcha_action: 'maintenance_subscription'
      });
      setStatus('success');
    } catch (error) {
      setErrors([getApiErrorMessage(error, 'Unable to subscribe to maintenance emails.')]);
      setStatus('error');
    }
  };

  const handleClose = () => {
    setStatus();
    setErrors([]);
    close();
  };

  return (
    <Modal isOpen={value} toggle={handleClose}>
      <div className="modal-header">
        <button className="close" onClick={handleClose}>
          <span>×</span>
        </button>
        <h5 className="modal-title text-center">Subscribe to Maintenance Emails</h5>
      </div>

      <div className="modal-body mb-1 p-0">
        {status === 'success' ? (
          <div className="px-5 py-4">
            <Alert color="success" className="mb-0">Subscription request sent.</Alert>
          </div>
        ) : (
          <AvForm onValidSubmit={handleValidSubmit}>
            <div className="px-5 py-3">
              <Row>
                <Col md={6}>
                  <AvField label="Name" placeholder="Jane Doe" type="text" name="name" required />
                </Col>
                <Col md={6}>
                  <AvField label="Company" placeholder="Example Ltd" type="text" name="company" required />
                </Col>
              </Row>

              <AvField label="Email Address" placeholder="jane@example.com" type="email" name="email" required />
              <CaptchaChallenge captcha={captcha} />
            </div>

            <div className="modal-footer p-2">
              <Button color="primary" type="submit" disabled={status === 'submitting'}>
                {status === 'submitting' ? 'Submitting...' : 'Subscribe'}
              </Button>
              <Button color="secondary" onClick={handleClose}>Cancel</Button>
            </div>
            {status === 'error' && errors.length > 0 && (
              <div className="text-danger text-center mb-3 mt-1">
                {errors.map((error, index) => (
                  <p className="mb-0" key={index}>{error}</p>
                ))}
              </div>
            )}
          </AvForm>
        )}
      </div>
    </Modal>
  );
};

export default ({data: {downloads, scheduled_maintenances}, showMaintenanceSubscription}) => {
  const [modal, setModal] = useState();
  const [modalOpen, setModalOpen] = useState();
  const [downloaded, setDownloaded] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const nextMaintenance = getNextMaintenance(scheduled_maintenances);

  const openModal = (whitepaper) => {
    setModal(whitepaper);
    setModalOpen(true);
    setDownloaded(false);
  }
  const closeModal = () => setModalOpen(false);

  return (
    <section className="section px-3 px-md-0" id="support" style={{background: '#7cb3d8', color: 'white'}}>

      <DownloadModal value={modalOpen} close={closeModal} file={modal} downloaded={downloaded} setDownloaded={setDownloaded} />
      <MaintenanceSubscriptionModal value={maintenanceModalOpen} close={() => setMaintenanceModalOpen(false)} />

      <Container className="text-center">
        <h2 className="title mb-5" style={{color: 'white'}}>EDIfly Solution Support and Maintenance</h2>
        <Row>
          
          {downloads ? Object.keys(downloads).map(key => {
            let files = downloads[key];
            return (
              <Col xs={12} md={4} key={key}>
                <h4 className="text-left"><b>{key}</b></h4> <br />
                  {files.map((file, index) => (
                    <p onClick={() => openModal(file)} className="text-left" key={index} style={{transform: 'rotate(0)'}}>
                      <a href="#support" className="stretched-link" style={{color: 'white'}}>{index+1}. {file.title}</a>
                    </p>
                  ))}
              </Col>)
          }) : ''}

        </Row>
        {showMaintenanceSubscription && (
          <div className="mt-5">
            <p className="mb-3" style={{fontSize: '1.35rem', fontWeight: 600}}>
              Next Scheduled Maintenance: {formatNextMaintenance(nextMaintenance)}.
            </p>
            <Button
              color="warning"
              size="lg"
              style={{color: '#1f4f6e', fontWeight: 700, boxShadow: '0 6px 18px rgba(0, 0, 0, 0.22)'}}
              onClick={() => setMaintenanceModalOpen(true)}
            >
              Subscribe to Maintenance Notifications
            </Button>
          </div>
        )}
      </Container>
    </section>
  )
}
